import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { cn } from '@/lib/utils';

interface ChatMarkdownProps {
  children: string;
  className?: string;
  variant?: 'assistant' | 'user';
}

export function ChatMarkdown({ children, className, variant = 'assistant' }: ChatMarkdownProps) {
  const markdownComponents: Components = {
    a({ children, href, ...props }) {
      return (
        <a {...props} href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    },
    pre({ children, className, ...props }) {
      return (
        <pre {...props} className={cn('overflow-x-auto', className)}>
          {children}
        </pre>
      );
    },
    table({ children, className, ...props }) {
      return (
        <div className={cn('w-full overflow-x-auto', variant === 'assistant' ? 'my-5' : 'my-2')}>
          <table {...props} className={cn('w-full', className)}>
            {children}
          </table>
        </div>
      );
    },
  };

  return (
    <div
      className={cn(
        'prose prose-sm prose-neutral max-w-none min-w-0 break-words text-foreground',
        'prose-headings:font-semibold',
        'prose-a:text-primary prose-a:underline-offset-4',
        'prose-code:break-words prose-pre:max-w-full prose-pre:rounded-lg',
        'prose-blockquote:border-border prose-blockquote:text-muted-foreground',
        'prose-table:my-0 prose-th:bg-muted/60',
        variant === 'assistant'
          ? 'prose-headings:mb-2 prose-headings:mt-4 prose-p:my-2 prose-li:my-0.5 prose-ul:my-2 prose-ol:my-2 prose-pre:my-3 prose-blockquote:my-3'
          : 'prose-headings:mb-1 prose-headings:mt-1.5 prose-p:my-0.5 prose-li:my-0 prose-ul:my-1 prose-ol:my-1 prose-pre:my-1.5 prose-blockquote:my-1.5',
        className
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
